import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Minus, Plus, CalendarHeart, Ruler, Sparkles, ArrowRight } from "lucide-react";
import { api, money } from "@/lib/api";
import { SITE } from "@/lib/seo";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1631005436794-ccaa79de61ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB3b21hbiUyMGt1cnRpJTIwZWxlZ2FudCUyMGZhc2hpb24lMjBlZGl0b3JpYWx8ZW58MHx8fHwxNzg2NDA5NjY3fDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: Sparkles, title: "Pick your styles", text: "Browse the pre-order catalog below and note the styles, sizes and quantities you love." },
  { icon: MessageCircle, title: "Message us on WhatsApp", text: "Tap the order button on any piece — your selection is pre-filled, ready to send." },
  { icon: CalendarHeart, title: "We confirm & pre-order", text: "We confirm group pricing, sizing and delivery timeline so everything arrives before the big day." },
];

export default function BridesmaidDresses() {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weddingDate, setWeddingDate] = useState("");
  const [selSizes, setSelSizes] = useState({});
  const [qtys, setQtys] = useState({});

  useEffect(() => {
    api.get("/bulk-pieces").then((r) => setPieces(r.data.pieces || []))
      .catch(() => setPieces([]))
      .finally(() => setLoading(false));
  }, []);

  const toggleSize = (id, size) =>
    setSelSizes((m) => {
      const cur = m[id] || [];
      return { ...m, [id]: cur.includes(size) ? cur.filter((s) => s !== size) : [...cur, size] };
    });

  const setQty = (id, delta) =>
    setQtys((m) => ({ ...m, [id]: Math.max(1, Math.min(99, (m[id] || 4) + delta)) }));

  const waLink = (p) => {
    const sel = selSizes[p.id] || [];
    const qty = qtys[p.id] || 4;
    const lines = [
      "Hi Kurti Connection! I'd like to place a bulk bridesmaid pre-order.",
      `• Dress: ${p.title}`,
      p.price != null ? `• Listed price: ${money(p.price)} each` : null,
      sel.length ? `• Sizes: ${sel.join(", ")}` : null,
      `• Quantity: ${qty}`,
      weddingDate ? `• Wedding date: ${weddingDate}` : null,
      "Please confirm availability, group pricing and delivery timeline.",
    ].filter(Boolean);
    return `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const genericWa = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
    "Hi Kurti Connection! I'm planning a wedding and would like to discuss a bulk order for bridesmaid dresses."
  )}`;

  return (
    <main data-testid="bridesmaid-page">
      <Seo
        title="Bulk Bridesmaid Dresses — 2026–27 Wedding Collection Pre-Orders"
        description="Now accepting bulk orders for bridesmaid dresses from our 2026–27 wedding collection. Browse special pre-order styles, pick sizes and quantities, and order directly on WhatsApp."
        path="/bridesmaid-dresses"
      />

      {/* Hero */}
      <section className="bg-[#1A1A1A] text-[#FFFFFF]" data-testid="bridesmaid-hero">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 items-stretch">
          <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-16 lg:py-24">
            <div className="overline text-[#B84A3B] mb-4">Now Accepting Bulk Orders · 2026–27 Wedding Collection</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
              Bridesmaid Dresses, Made for Your Big Day
            </h1>
            <p className="mt-5 text-base text-[#FFFFFF]/80 font-body leading-relaxed max-w-lg">
              Matching outfits for your whole bridal party — pre-order special styles in the sizes you need.
              Every piece below is made to order, so message us early and we&apos;ll have everyone wedding-ready on time.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={genericWa} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-cta"
                className="inline-flex items-center gap-2 bg-[#25D366] text-[#0b3d1e] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:opacity-90 transition-opacity">
                <MessageCircle size={15} /> Chat on WhatsApp
              </a>
              <a href="#catalog" data-testid="hero-browse-cta"
                className="inline-flex items-center gap-2 border border-[#FFFFFF]/40 px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:bg-[#FFFFFF] hover:text-[#1A1A1A] transition-colors">
                Browse Styles <ArrowRight size={14} />
              </a>
            </div>
          </div>
          <div className="relative min-h-[320px] lg:min-h-[540px]">
            <img src={HERO_IMG} alt="Elegant festive Indian wear for bridesmaids" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-[#E5E5DF]" data-testid="how-it-works">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="flex items-start gap-4">
                <span className="font-mono text-[0.65rem] tracking-[0.2em] text-[#B84A3B] border border-[#B84A3B] rounded-full w-9 h-9 flex items-center justify-center shrink-0">{`0${i + 1}`}</span>
                <div>
                  <div className="font-body text-sm font-semibold flex items-center gap-2"><s.icon size={15} className="text-[#B84A3B]" />{s.title}</div>
                  <p className="text-[0.8rem] text-[#6E6E69] mt-1.5 leading-relaxed">{s.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Catalog */}
      <section id="catalog" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14" data-testid="bulk-catalog">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight">The Pre-Order Catalog</h2>
              <p className="text-sm text-[#6E6E69] mt-1">Special-order pieces — choose sizes &amp; quantity, then order on WhatsApp.</p>
            </div>
            <label className="block" data-testid="wedding-date-field">
              <span className="overline text-[#1A1A1A] block mb-1.5">Your wedding date (optional)</span>
              <input type="date" value={weddingDate} onChange={(e) => setWeddingDate(e.target.value)}
                data-testid="wedding-date-input"
                className="border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A] bg-white" />
            </label>
          </div>
        </Reveal>

        {loading ? (
          <div className="py-20 text-center text-sm text-[#6E6E69] font-body" data-testid="catalog-loading">Loading styles…</div>
        ) : pieces.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-[#E5E5DF]" data-testid="catalog-empty">
            <Ruler size={28} strokeWidth={1.3} className="mx-auto text-[#B84A3B]" />
            <p className="mt-4 font-body text-sm text-[#6E6E69] max-w-md mx-auto">
              Our bridesmaid pre-order catalog is being curated right now. Message us on WhatsApp with your
              wedding date and party size — we&apos;ll share the latest styles directly.
            </p>
            <a href={genericWa} target="_blank" rel="noreferrer" data-testid="empty-whatsapp-cta"
              className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-[#0b3d1e] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:opacity-90 transition-opacity">
              <MessageCircle size={15} /> Ask on WhatsApp
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12">
            {pieces.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.06}>
                <article className="group" data-testid={`piece-card-${p.id}`}>
                  <div className="relative overflow-hidden bg-[#EFEFEA] aspect-[3/4]">
                    {p.slug ? (
                      <Link to={`/product/${p.slug}`} data-testid={`piece-link-${p.id}`}>
                        <img src={p.image} alt={p.title} loading="lazy"
                          className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700" />
                      </Link>
                    ) : (
                      <img src={p.image} alt={p.title} loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700" />
                    )}
                    <span className="absolute top-3 left-3 bg-[#1A1A1A] text-white text-[0.58rem] font-mono tracking-[0.18em] uppercase px-2.5 py-1">Pre-Order</span>
                  </div>
                  <div className="pt-4">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-xl leading-tight">{p.title}</h3>
                      <span className="font-body text-sm font-semibold whitespace-nowrap" data-testid={`piece-price-${p.id}`}>
                        {p.price != null ? `From ${money(p.price)}` : "Ask for price"}
                      </span>
                    </div>
                    {p.description && <p className="text-[0.8rem] text-[#6E6E69] mt-1.5 leading-relaxed">{p.description}</p>}

                    {p.sizes?.length > 0 && (
                      <div className="mt-3.5">
                        <span className="overline text-[0.6rem] text-[#6E6E69] block mb-2">Select sizes</span>
                        <div className="flex flex-wrap gap-1.5" data-testid={`piece-sizes-${p.id}`}>
                          {p.sizes.map((s) => {
                            const on = (selSizes[p.id] || []).includes(s);
                            return (
                              <button key={s} onClick={() => toggleSize(p.id, s)} data-testid={`size-${p.id}-${s}`}
                                className={`min-w-[2.4rem] px-2 py-1.5 text-[0.7rem] font-mono border transition-colors ${on ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5DF] hover:border-[#1A1A1A]"}`}>
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex items-center gap-4">
                      <div className="flex items-center border border-[#E5E5DF]" data-testid={`qty-${p.id}`}>
                        <button onClick={() => setQty(p.id, -1)} aria-label="Decrease quantity" data-testid={`qty-minus-${p.id}`}
                          className="px-2.5 py-2 hover:bg-[#EFEFEA] transition-colors"><Minus size={13} /></button>
                        <span className="w-9 text-center text-sm font-body" data-testid={`qty-value-${p.id}`}>{qtys[p.id] || 4}</span>
                        <button onClick={() => setQty(p.id, 1)} aria-label="Increase quantity" data-testid={`qty-plus-${p.id}`}
                          className="px-2.5 py-2 hover:bg-[#EFEFEA] transition-colors"><Plus size={13} /></button>
                      </div>
                      <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid={`order-whatsapp-${p.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-[#0b3d1e] px-4 py-3 text-[0.66rem] font-mono tracking-[0.16em] uppercase hover:opacity-90 transition-opacity">
                        <MessageCircle size={14} /> Order on WhatsApp
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#B84A3B] text-[#FFFFFF]" data-testid="bridesmaid-bottom-cta">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Planning a 2026–27 wedding?</h2>
            <p className="mt-2 text-sm text-[#FFFFFF]/85 font-body max-w-xl">
              The earlier you pre-order, the more styles and sizes we can secure for your bridal party.
              Send us your date and party size — we&apos;ll take it from there.
            </p>
          </div>
          <a href={genericWa} target="_blank" rel="noreferrer" data-testid="bottom-whatsapp-cta"
            className="inline-flex items-center gap-2.5 bg-[#FFFFFF] text-[#1A1A1A] px-8 py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300 shrink-0">
            <MessageCircle size={15} /> Message Us <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </main>
  );
}
